/**
 * CarePlanDetail — read-only detail panel for a single care plan.
 *
 * Phase 3 (Care Plan v2) extraction. Renders the legacy fields plus the v2
 * extensions (linked goals/risks/restrictive practices/medications, structured
 * sections, acknowledgement table). v2 fields render only when present in the
 * payload — backend gates that with the `care_plan_v2_enabled` flag.
 */
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { CarePlanPortalPublishPanel } from './CarePlanPortalPublishPanel';

const LEGACY_SECTIONS = [
  ['communication_preferences', 'Communication Preferences'],
  ['mobility_support', 'Mobility Support'],
  ['personal_care_needs', 'Personal Care Needs'],
  ['dietary_requirements', 'Dietary Requirements'],
  ['community_access_support', 'Community Access'],
  ['behaviour_support_strategies', 'Behaviour Support'],
  ['medical_alerts', 'Medical Alerts'],
  ['allergies', 'Allergies'],
  ['emergency_contacts', 'Emergency Contacts'],
  ['routine_morning', 'Morning Routine'],
  ['routine_afternoon', 'Afternoon Routine'],
  ['routine_evening', 'Evening Routine'],
  ['routine_night', 'Night Routine'],
  ['important_notes', 'Important Notes'],
];

function LinkedBlock({ title, items, emptyHint, testid }) {
  return (
    <div data-testid={testid}>
      <h5 className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{title}</h5>
      {items && items.length > 0 ? (
        <ul className="text-sm space-y-0.5">
          {items.map((it, i) => (
            <li key={it.id || i} className="flex items-center gap-2">
              <span className="font-mono text-[10px] text-muted-foreground">{it.no || it.id}</span>
              <span>{it.label || it.title || it.name}</span>
              {it.severity && <Badge variant="outline" className="text-[10px]">{it.severity}</Badge>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground italic">{emptyHint}</p>
      )}
    </div>
  );
}

export function CarePlanDetail({ plan, onClose, linkedGoals, linkedRisks,
                                linkedRestrictivePractices, linkedMedications,
                                acknowledgements, readOnly = false }) {
  if (!plan) return null;
  const isV2 = !!plan.sections || !!plan.linked_goal_ids || !!plan.acknowledgements;

  return (
    <Card data-testid="care-plan-detail">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="font-mono text-[10px]">{plan.plan_no}</Badge>
              <StatusBadge status={plan.status} />
              <span className="text-xs text-muted-foreground">v{plan.version}</span>
              {plan.review_due_date && (
                <span className="text-xs text-muted-foreground">Review {plan.review_due_date}</span>
              )}
            </div>
            <h3 className="font-semibold">{plan.title}</h3>
          </div>
          {onClose && (
            <Button size="sm" variant="ghost" onClick={onClose} data-testid="care-plan-detail-close">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* v2 linked entities — rendered only when v2 flag-ON payload arrived */}
        {isV2 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 border-t pt-3">
            <LinkedBlock title="Linked Goals" items={linkedGoals}
                         emptyHint="No goals linked" testid="linked-goals" />
            <LinkedBlock title="Linked Risks" items={linkedRisks}
                         emptyHint="No risks linked" testid="linked-risks" />
            <LinkedBlock title="Restrictive Practices" items={linkedRestrictivePractices}
                         emptyHint="None linked" testid="linked-restrictive-practices" />
            <LinkedBlock title="Medication Support" items={linkedMedications}
                         emptyHint="No medication links" testid="linked-medications" />
          </div>
        )}

        {/* Structured v2 sections (preferred), else legacy flat fields */}
        {isV2 && Array.isArray(plan.sections) && plan.sections.length > 0 ? (
          <div className="space-y-3 border-t pt-3" data-testid="care-plan-sections">
            {plan.sections.map((s, i) => (
              <div key={s.key || i}>
                <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {s.label || s.key}
                </h5>
                <p className="text-sm whitespace-pre-wrap">{s.content || '—'}</p>
                {s.updated_at && (
                  <p className="text-[10px] text-muted-foreground">
                    Last updated {String(s.updated_at).slice(0, 16)}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 border-t pt-3 text-sm">
            {LEGACY_SECTIONS.map(([k, label]) => (
              plan[k] ? (
                <div key={k}>
                  <h5 className="text-xs font-semibold text-muted-foreground">{label}</h5>
                  <p>{plan[k]}</p>
                </div>
              ) : null
            ))}
          </div>
        )}

        {/* Acknowledgements table */}
        {isV2 && Array.isArray(acknowledgements) && (
          <div className="border-t pt-3" data-testid="care-plan-acknowledgements">
            <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
              Acknowledgements ({acknowledgements.length})
            </h5>
            {acknowledgements.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No acknowledgements recorded yet.</p>
            ) : (
              <ul className="text-sm space-y-0.5">
                {acknowledgements.map((a, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">{a.role}</Badge>
                    <span>{a.signer_name}</span>
                    {a.witness_name && (
                      <span className="text-xs text-muted-foreground">witness: {a.witness_name}</span>
                    )}
                    <span className="ml-auto text-[10px] text-muted-foreground">
                      {String(a.timestamp).slice(0, 16)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Phase 6: portal publish panel (renders only when flag ON + plan APPROVED).
            Suppressed in read-only surfaces (e.g. Support Worker profile viewer). */}
        {!readOnly && <CarePlanPortalPublishPanel plan={plan} />}
      </CardContent>
    </Card>
  );
}
